import { useCallback, useRef, useState, useTransition } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { PaymentInformationData, savePaymentInformation, setNeedsReview } from "store/application/applicationSlice";
import {
    submitCreditCardPaymentValidationRequest,
    submitEftPaymentValidationRequest,
    type CreditCardPaymentRequest,
    type BankTransferPaymentRequest,
} from "services/paymentTokenizationService";
import { pushGTMEvent } from "utils/gtmEvent";
import { GA_EVENT_CONFIGS } from "constants/gaEvents";

export type CreditCardFormData = {
    nameOnCard: string;
    cardNumber: string;
    csv: string;
    expiry: string;
};

export type BankTransferFormData = {
    nameOnAccount: string;
    routingNumber: string;
    accountNumber: string;
    accountType: "checking" | "savings";
};

export type PaymentFormData = CreditCardFormData | BankTransferFormData;

interface UsePaymentSubmissionReturn {
    paymentData: PaymentInformationData | null;
    submitPayment: (data: PaymentFormData, paymentMethod: "creditCard" | "bankTransfer") => Promise<void>;
    isLoading: boolean;
    error: string | null;
}

export const usePayment = (): UsePaymentSubmissionReturn => {
    const dispatch = useAppDispatch();
    const [isPending, startTransition] = useTransition();
    const [error, setError] = useState<string | null>(null);
    const isSubmittingRef = useRef(false);

    const contactData = useAppSelector((state) => state.application.contactData);
    const paymentRefId = contactData?.policyNumber;
    const paymentData = useAppSelector((state) => state.application.paymentInformationData);

    const submitPayment = useCallback(
        (data: PaymentFormData, paymentMethod: "creditCard" | "bankTransfer"): Promise<void> => {
            if (isSubmittingRef.current) return Promise.resolve();

            if (!paymentRefId) {
                setError("***Payment reference ID is required");
                dispatch(setNeedsReview(true));
                return Promise.resolve();
            }

            setError(null);
            isSubmittingRef.current = true;

            const nameOnAccount =
                paymentMethod === "creditCard"
                    ? (data as CreditCardFormData).nameOnCard
                    : (data as BankTransferFormData).nameOnAccount;

            const lastFourDigits =
                paymentMethod === "creditCard"
                    ? (data as CreditCardFormData).cardNumber.slice(-4)
                    : (data as BankTransferFormData).accountNumber.slice(-4);

            return new Promise<void>((resolve, reject) => {
                startTransition(async () => {
                    try {
                        let paymentTokenResponse;

                        if (paymentMethod === "creditCard") {
                            const ccData = data as CreditCardFormData;
                            const request: CreditCardPaymentRequest = {
                                paymentRefId,
                                nameOnCard: ccData.nameOnCard,
                                cardNumber: ccData.cardNumber,
                                csv: ccData.csv,
                                expiry: ccData.expiry,
                            };
                            paymentTokenResponse = await submitCreditCardPaymentValidationRequest(request);
                            pushGTMEvent(GA_EVENT_CONFIGS.PAYMENT_CC_SUCCESS);
                        } else {
                            const bankData = data as BankTransferFormData;
                            const request: BankTransferPaymentRequest = {
                                paymentRefId,
                                nameOnAccount: bankData.nameOnAccount,
                                accountType: bankData.accountType,
                                accountNumber: bankData.accountNumber,
                                routingNumber: bankData.routingNumber,
                            };
                            paymentTokenResponse = await submitEftPaymentValidationRequest(request);
                            pushGTMEvent(GA_EVENT_CONFIGS.PAYMENT_BANK_SUCCESS);
                        }

                        const paymentToken = paymentTokenResponse.paymentToken;

                        dispatch(
                            savePaymentInformation({
                                paymentToken,
                                paymentType: paymentMethod === "creditCard" ? "CC" : "ET",
                                nameOnAccount,
                                lastFourDigits,
                            })
                        );

                        resolve();
                    } catch (err) {
                        const errorMessage = err instanceof Error ? err.message : "Payment tokenization failed";
                        setError(errorMessage);
                        dispatch(setNeedsReview(true));

                        if (paymentMethod === "creditCard") {
                            pushGTMEvent(GA_EVENT_CONFIGS.PAYMENT_CC_FAILED);
                        } else {
                            pushGTMEvent(GA_EVENT_CONFIGS.PAYMENT_BANK_FAILED);
                        }

                        reject(err);
                    } finally {
                        isSubmittingRef.current = false;
                    }
                });
            });
        },
        [dispatch, paymentRefId, startTransition]
    );

    return {
        paymentData,
        submitPayment,
        isLoading: isPending,
        error,
    };
};
